import { useMemo, useState } from "react";
import * as XLSX from "xlsx";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { importSuppliers, validateSuppliersImport } from "../api/cards";

const HEADERS = {
  supplier_name: ["tedarikci adi", "tedarikçi adı", "tedarikci ad", "tedarikçi ad", "ad", "isim"],
  supplier_code: ["tedarikci kodu", "tedarikçi kodu", "kod"],
  phone: ["telefon", "tel", "phone"],
  address: ["adres", "address"],
};

function normalizeHeaderKey(x) {
  return String(x || "")
    .trim()
    .toLocaleLowerCase("tr")
    .replace(/ı/g, "i")
    .replace(/ş/g, "s")
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c")
    .replace(/\s+/g, " ");
}

function findHeaderIndex(headers, aliases) {
  const norm = headers.map(normalizeHeaderKey);
  for (const a of aliases) {
    const idx = norm.indexOf(normalizeHeaderKey(a));
    if (idx >= 0) return idx;
  }
  return -1;
}

export default function SuppliersImportModal({ onClose, onImported }) {
  const [fileName, setFileName] = useState("");
  const [parseError, setParseError] = useState("");
  const [rows, setRows] = useState([]);

  const [validating, setValidating] = useState(false);
  const [validated, setValidated] = useState(null);

  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [result, setResult] = useState(null);

  const errorCount = useMemo(() => (validated?.errors || []).length, [validated]);
  const canSubmit = !!rows.length && !validating && !saving && errorCount === 0 && !!validated;

  async function handlePickFile(file) {
    setParseError("");
    setSubmitError("");
    setResult(null);
    setRows([]);
    setValidated(null);
    setFileName(file?.name || "");
    if (!file) return;

    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array", cellDates: true });
      const sheetName = wb.SheetNames?.[0];
      if (!sheetName) throw new Error("Excel sayfası bulunamadı");
      const ws = wb.Sheets[sheetName];

      const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
      if (!Array.isArray(aoa) || aoa.length < 2) {
        throw new Error("Excel boş veya başlık satırı eksik");
      }

      const headerRow = (aoa[0] || []).map((x) => String(x || "").trim());
      const idx = {
        supplier_name: findHeaderIndex(headerRow, HEADERS.supplier_name),
        supplier_code: findHeaderIndex(headerRow, HEADERS.supplier_code),
        phone: findHeaderIndex(headerRow, HEADERS.phone),
        address: findHeaderIndex(headerRow, HEADERS.address),
      };

      if (idx.supplier_name < 0) {
        throw new Error('Eksik zorunlu sütun: "Tedarikçi Adı"');
      }

      const parsed = [];
      for (let i = 1; i < aoa.length; i += 1) {
        const r = aoa[i] || [];
        const get = (k) => (idx[k] >= 0 ? String(r[idx[k]] ?? "").trim() : "");
        const rec = {
          supplier_name: get("supplier_name"),
          supplier_code: get("supplier_code"),
          phone: get("phone"),
          address: get("address"),
        };
        if (Object.values(rec).every((v) => !v)) continue;
        parsed.push(rec);
      }

      if (!parsed.length) {
        setParseError("Dosyada veri satırı bulunamadı.");
        return;
      }

      setRows(parsed);

      setValidating(true);
      try {
        const res = await validateSuppliersImport(parsed);
        setValidated(res);
      } catch (err) {
        console.error("SUPPLIERS VALIDATE ERROR", err);
        setParseError(err?.response?.data?.error?.message || err?.message || "Doğrulama başarısız");
      } finally {
        setValidating(false);
      }
    } catch (err) {
      console.error("EXCEL PARSE ERROR", err);
      setParseError(err?.message || "Excel okunamadı");
    }
  }

  async function submit() {
    setSubmitError("");
    setResult(null);
    if (!rows.length) return;
    if (errorCount > 0) {
      setSubmitError("Hatalı satırlar var. Lütfen düzeltin.");
      return;
    }

    setSaving(true);
    try {
      const res = await importSuppliers(rows);
      setResult(res);
      await onImported?.(res);
    } catch (err) {
      console.error("SUPPLIERS IMPORT ERROR", err);
      const status = err?.response?.status;
      const dataObj = err?.response?.data;
      const message =
        dataObj?.error?.message ||
        dataObj?.message ||
        (typeof dataObj === "string" ? dataObj : null) ||
        err?.message ||
        null;
      setSubmitError(`İçe aktarılamadı${status ? ` (HTTP ${status})` : ""}${message ? `: ${message}` : ""}`);
    } finally {
      setSaving(false);
    }
  }

  const rowErrorMap = useMemo(() => {
    const m = new Map();
    for (const e of validated?.errors || []) {
      m.set(Number(e.row_index), e.errors);
    }
    return m;
  }, [validated]);

  return (
    <Dialog open onClose={saving ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          Tedarikçi Excel İçe Aktar
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Sütunlar: <b>Tedarikçi Adı</b> (zorunlu), <b>Tedarikçi Kodu</b>, <b>Telefon</b>, <b>Adres</b>.
          Tedarikçi Kodu varsa kodla, yoksa adla eşleştirme yapılır (varsa güncellenir, yoksa eklenir).
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {parseError ? <Alert severity="error">{parseError}</Alert> : null}
          {submitError ? <Alert severity="error">{submitError}</Alert> : null}

          {result ? (
            <Alert severity={result?.errors?.length ? "warning" : "success"}>
              Yeni tedarikçi: <b>{result.suppliers_created || 0}</b>, güncellenen: <b>{result.suppliers_updated || 0}</b>
              {result?.errors?.length ? (
                <>
                  <br />
                  Hata: <b>{result.errors.length}</b>
                  <ul style={{ margin: "4px 0 0 16px" }}>
                    {result.errors.map((e, i) => (
                      <li key={i}>
                        <b>{e.supplier_code || e.supplier_name}</b>: {e.message}
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
            </Alert>
          ) : null}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
            <Button variant="outlined" component="label" disabled={saving}>
              Dosya Seç (.xlsx)
              <input
                type="file"
                hidden
                accept=".xlsx,.xls,.csv"
                onChange={(e) => handlePickFile(e.target.files?.[0] || null)}
              />
            </Button>
            <Typography variant="body2" color="text.secondary">
              {fileName ? `Seçili: ${fileName}` : "Henüz dosya seçilmedi"}
              {validating ? " (doğrulanıyor...)" : ""}
            </Typography>
          </Stack>

          {rows.length ? (
            <Box sx={{ overflowX: "auto" }}>
              <Typography variant="body2" color={errorCount > 0 ? "error" : "text.secondary"}>
                Satır: <b>{rows.length}</b> — Hata: <b>{errorCount}</b>
              </Typography>
              <Table size="small" sx={{ mt: 1 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Ad</TableCell>
                    <TableCell>Kod</TableCell>
                    <TableCell>Telefon</TableCell>
                    <TableCell>Adres</TableCell>
                    <TableCell>Durum</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(validated?.resolved || rows.map((r, i) => ({ ...r, row_index: i }))).map((r, idx) => {
                    const errs = rowErrorMap.get(Number(r.row_index ?? idx));
                    return (
                      <TableRow key={idx} hover>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>{r.supplier_name || "—"}</TableCell>
                        <TableCell>{r.supplier_code || "—"}</TableCell>
                        <TableCell>{r.phone || "—"}</TableCell>
                        <TableCell sx={{ maxWidth: 300, whiteSpace: "normal" }}>
                          {r.address || "—"}
                        </TableCell>
                        <TableCell>
                          {errs && errs.length ? (
                            <Typography variant="caption" color="error">
                              {errs.join(", ")}
                            </Typography>
                          ) : validated ? (
                            <Typography variant="caption" color="success.main">
                              OK
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>
          Kapat
        </Button>
        <Button onClick={submit} variant="contained" disabled={!canSubmit}>
          {saving ? "Oluşturuluyor..." : "Oluştur"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
